import { acceptsMediaRevision, type MediaCapture } from "./types";

// Separate from scene snapshots: a late scene save cannot revert media ownership.
let database: Promise<IDBDatabase> | null = null;
function openDatabase(): Promise<IDBDatabase> {
  if (database) return database;
  database = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("Media cache is unavailable")); return; }
    const request = indexedDB.open("vaon-media", 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      const captures = db.createObjectStore("captures", { keyPath: "key" });
      captures.createIndex("projectId", "projectId");
      const files = db.createObjectStore("files", { keyPath: "key" });
      files.createIndex("projectId", "projectId");
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => { request.result.close(); database = null; };
      resolve(request.result);
    };
    request.onerror = () => reject(request.error ?? new Error("Media cache could not be opened"));
    request.onblocked = () => reject(new Error("Close older VAON tabs to update the media cache"));
  }).catch(error => { database = null; throw error; });
  return database;
}
function finished(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = tx.onabort = () => reject(tx.error ?? new Error("Media cache transaction failed"));
  });
}
export async function cacheMedia(projectId: string, captures: MediaCapture[], files = new Map<string, Uint8Array>()): Promise<void> {
  const db = await openDatabase();
  const tx = db.transaction(["captures", "files"], "readwrite");
  const completion = finished(tx);
  const store = tx.objectStore("captures");
  for (const capture of captures) {
    const key = `${projectId}/${capture.id}`;
    const read = store.get(key);
    read.onsuccess = () => {
      if (acceptsMediaRevision(read.result?.capture, capture)) store.put({ key, projectId, capture });
    };
  }
  for (const [id, bytes] of files) tx.objectStore("files").put({ key: `${projectId}/${id}`, projectId, bytes });
  await completion;
}
export async function readCachedMedia(projectId: string): Promise<MediaCapture[]> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = db.transaction("captures", "readonly").objectStore("captures").index("projectId").getAll(projectId);
    request.onsuccess = () => resolve(request.result.map(row => row.capture));
    request.onerror = () => reject(request.error);
  });
}
export async function readCachedMediaFile(projectId: string, fileId: string): Promise<Uint8Array | null> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = db.transaction("files", "readonly").objectStore("files").get(`${projectId}/${fileId}`);
    request.onsuccess = () => resolve(request.result?.bytes ?? null);
    request.onerror = () => reject(request.error);
  });
}

/** Remove only this project's canonical records and immutable cached originals. */
export async function deleteCachedMedia(projectId: string): Promise<void> {
  const db = await openDatabase();
  const tx = db.transaction(["captures", "files"], "readwrite");
  const completion = finished(tx);
  for (const name of ["captures", "files"]) {
    const request = tx.objectStore(name).index("projectId").openCursor(IDBKeyRange.only(projectId));
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) { cursor.delete(); cursor.continue(); }
    };
  }
  await completion;
}
